import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { KlaviyoForm } from '@/scripts/theme/klaviyo-form'
import { ModalDialog } from '@/scripts/theme/modal-dialog'

type WaitlistOperation = 'backInStock' | 'list'
type WaitlistRequest = {
	name: WaitlistOperation
	request: () => Promise<Response>
}

export function waitlistSubmissionKey(
	companyId: string,
	email: string,
	variant: string,
	listId: string
) {
	return JSON.stringify([companyId, email, variant, listId])
}

export async function runWaitlistRequests(
	requests: WaitlistRequest[],
	accepted: Set<WaitlistOperation>
) {
	const pending = requests.filter(({ name }) => !accepted.has(name))
	const settled = await Promise.allSettled(
		pending.map(async ({ name, request }) => {
			const response = await request()
			if (!response.ok) {
				const responseBody = await response.text()
				throw new Error(
					responseBody || `Klaviyo request failed: ${response.status}`
				)
			}
			accepted.add(name)
			return name
		})
	)
	return pending.flatMap(({ name }, index) =>
		settled[index]?.status === 'rejected' ? [name] : []
	)
}

export class WaitlistForm extends UcoastEl {
	static htmlSelector = 'waitlist-form'
	form: HTMLFormElement
	submitButton: HTMLButtonElement
	emailInput: HTMLInputElement
	emailField: HTMLElement
	phoneField: HTMLElement
	variantInput: HTMLInputElement
	successMessage: string
	submittedLabel: string
	submitLabel: string
	errorMessage: string
	backInStockErrorMessage: string
	listErrorMessage: string
	companyId: string
	productTitle: string | undefined
	isSubmitting = false
	acceptedBySubmission = new Map<string, Set<WaitlistOperation>>()

	constructor() {
		super()
		this.form = q.rs('form', this)
		this.submitButton = q.rs('[type="submit"]', this)
		this.variantInput = q.rs('[name="variant"]', this)
		this.emailInput = q.rs('[type="email"]', this)
		this.emailField = q.rs('.field', this)
		this.phoneField = q.rs('.waitlist__phone-field', this)
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
		this.successMessage = q.ra(this, 'data-success')
		this.submittedLabel = q.ra(this, 'data-submitted')
		this.submitLabel = this.submitButton.textContent?.trim() ?? ''
		this.errorMessage = q.ra(this, 'data-error')
		this.backInStockErrorMessage = q.ra(this, 'data-back-in-stock-error')
		this.listErrorMessage = q.ra(this, 'data-list-error')
		this.companyId = q.ra(this, 'data-company-id')
		this.productTitle = q.oa(this, 'data-product-title')
		if (this.productTitle) this.updateProductTitle(this.productTitle)
	}

	prepareForSubmission() {
		this.form.reset()
		this.emailField.hidden = false
		this.phoneField.hidden = false
		this.emailInput.removeAttribute('aria-invalid')
		this.submitButton.textContent = this.submitLabel
		this.submitButton.disabled = false
		this.submitButton.removeAttribute('aria-disabled')
		this.submitButton.classList.remove('success', 'button--outline')
		const status = q.rs<HTMLElement>('[data-form-status]', this)
		status.textContent = ''
		status.classList.add('hidden')
		delete this.dataset.state
	}

	getTitleElement() {
		return this.closest(
			'.product-popup-modal__content-info'
		)?.querySelector<HTMLElement>('.product-title')
	}

	updateProductTitle(productTitle?: string) {
		const titleEl = this.getTitleElement()
		if (titleEl) titleEl.textContent = productTitle ?? ''
	}

	showStatus(message: string, state: 'success' | 'error') {
		const status = q.rs<HTMLElement>('[data-form-status]', this)
		status.textContent = message
		status.setAttribute('role', state === 'error' ? 'alert' : 'status')
		status.setAttribute(
			'aria-live',
			state === 'error' ? 'assertive' : 'polite'
		)
		status.classList.remove('hidden')
		this.dataset.state = state
	}

	async onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.isSubmitting || this.emailInput.value.length < 1) return
		this.isSubmitting = true
		this.form.setAttribute('aria-busy', 'true')
		this.submitButton.disabled = true
		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.classList.add('loading')

		const formData = new FormData(this.form)
		const email = q.rfd(formData, 'email')
		const variant = q.rfd(formData, 'variant')
		const listId = q.ofd(formData, 'list_id') ?? ''
		const submissionKey = waitlistSubmissionKey(
			this.companyId,
			email,
			variant,
			listId
		)
		const accepted =
			this.acceptedBySubmission.get(submissionKey) ??
			new Set<WaitlistOperation>()
		this.acceptedBySubmission.set(submissionKey, accepted)

		const requests: WaitlistRequest[] = [
			{
				name: 'backInStock',
				request: () =>
					fetch(
						`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`,
						this.notifyMeConfig(formData)
					),
			},
		]
		if (listId) {
			requests.push({
				name: 'list',
				request: () =>
					fetch(
						`https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`,
						KlaviyoForm.addToKlaviyoListConfig(formData)
					),
			})
		}

		try {
			const failures = await runWaitlistRequests(requests, accepted)
			if (failures.length > 0) {
				const message = accepted.has('backInStock')
					? this.listErrorMessage
					: accepted.has('list')
						? this.backInStockErrorMessage
						: this.errorMessage
				this.emailInput.setAttribute('aria-invalid', 'true')
				this.showStatus(message, 'error')
				return
			}
			this.emailInput.removeAttribute('aria-invalid')
			this.form.reset()
			this.emailField.hidden = true
			this.phoneField.hidden = true
			this.submitButton.textContent = this.submittedLabel
			this.submitButton.classList.add('success', 'button--outline')
			this.showStatus(this.successMessage, 'success')
			window.setTimeout(
				() => q.rs<ModalDialog>('#WaitlistModal')?.hide(),
				5000
			)
		} catch (error) {
			this.emailInput.setAttribute('aria-invalid', 'true')
			this.showStatus(this.errorMessage, 'error')
			console.error(error)
		} finally {
			this.isSubmitting = false
			this.form.removeAttribute('aria-busy')
			this.submitButton.classList.remove('loading')
			if (this.dataset.state !== 'success') {
				this.submitButton.disabled = false
				this.submitButton.removeAttribute('aria-disabled')
			}
		}
	}

	notifyMeConfig(body: FormData) {
		const variant = q.rfd(body, 'variant')
		const data = {
			data: {
				type: 'back-in-stock-subscription',
				attributes: {
					profile: {
						data: {
							type: 'profile',
							attributes: { email: q.rfd(body, 'email') },
						},
					},
					channels: ['EMAIL'],
				},
				relationships: {
					variant: {
						data: {
							type: 'catalog-variant',
							id: `$shopify:::$default:::${variant}`,
						},
					},
				},
			},
		}
		return {
			method: 'POST',
			mode: 'cors' as RequestMode,
			headers: {
				'Content-Type': 'application/json',
				Revision: '2026-07-15',
			},
			body: JSON.stringify(data),
		}
	}
}
